import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { DEVICE_PRESETS } from '@busswerks/shared'
import type { DevicePreset } from '@busswerks/shared'
import type { DeviceCategory, SignalType } from '@/types/devices'

interface CatalogEntrySummary {
  name: string
  manufacturer: string
  category: string
  type: string
  ports: string
}

interface AIDeviceMatch {
  catalogIndex: number
  confidence: number
  reason: string
}

interface AIPortGuess {
  name: string
  signalType: string
  connectorFormat?: string
  bidirectional?: boolean
}

interface AIDeviceGuess {
  manufacturer: string
  name: string
  category: string
  type: string
  inputs: AIPortGuess[]
  outputs: AIPortGuess[]
  confidence: number
  reason: string
}

interface IdentifyResponse {
  matches: AIDeviceMatch[]
  guess?: AIDeviceGuess
  modelUsed?: string
}

interface JobPostResponse {
  jobId: string
  status: 'pending'
}

interface JobStatusResponse {
  jobId: string
  status: 'pending' | 'done' | 'failed'
  result?: IdentifyResponse
  error?: string
}

export interface ResolvedMatch {
  preset: DevicePreset
  confidence: number
  reason: string
}

export interface ResolvedGuess {
  manufacturer: string
  name: string
  category: DeviceCategory
  type: string
  inputs: Array<{
    name: string
    signalType: SignalType
    connectorFormat?: string
    bidirectional?: boolean
  }>
  outputs: Array<{
    name: string
    signalType: SignalType
    connectorFormat?: string
    bidirectional?: boolean
  }>
  confidence: number
  reason: string
}

export interface IdentifyResult {
  matches: ResolvedMatch[]
  guess: ResolvedGuess | null
}

const VALID_CATEGORIES = new Set<DeviceCategory>([
  'interface',
  'mixer',
  'synth',
  'drum_machine',
  'sampler',
  'controller',
  'effects',
  'monitoring',
  'midi_interface',
  'groovebox',
  'semi_modular',
  'computer',
  'usb_hub',
  'network_switch',
  'recorder',
  'microphone',
])

const VALID_SIGNAL_TYPES = new Set<SignalType>([
  'audio',
  'midi',
  'sync',
  'cv_gate',
  'adat',
  'word_clock',
  'usb',
  'ethernet',
])

function normalizeSignalType(s: string): SignalType {
  return VALID_SIGNAL_TYPES.has(s as SignalType) ? (s as SignalType) : 'audio'
}

function normalizeCategory(s: string): DeviceCategory {
  return VALID_CATEGORIES.has(s as DeviceCategory)
    ? (s as DeviceCategory)
    : 'interface'
}

function normalizeGuess(g: AIDeviceGuess | undefined): ResolvedGuess | null {
  if (!g) return null
  return {
    manufacturer: g.manufacturer,
    name: g.name,
    category: normalizeCategory(g.category),
    type: g.type,
    inputs: g.inputs.map(p => ({
      name: p.name,
      signalType: normalizeSignalType(p.signalType),
      connectorFormat: p.connectorFormat,
      bidirectional: p.bidirectional,
    })),
    outputs: g.outputs.map(p => ({
      name: p.name,
      signalType: normalizeSignalType(p.signalType),
      connectorFormat: p.connectorFormat,
      bidirectional: p.bidirectional,
    })),
    confidence: g.confidence,
    reason: g.reason,
  }
}

const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 75_000

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Build the compact catalog summary the backend expects. Ports are
 * flattened into a short human-readable string (e.g.
 * "8x audio in, 8x audio out, 1x usb") that gives Claude enough
 * context to disambiguate visually similar devices without blowing
 * up the token budget with per-port objects.
 */
function buildCatalogSummary(): CatalogEntrySummary[] {
  return DEVICE_PRESETS.map(preset => {
    const portCounts = new Map<string, { in: number; out: number }>()
    for (const p of preset.inputs) {
      const bucket = portCounts.get(p.signalType) ?? { in: 0, out: 0 }
      bucket.in += 1
      portCounts.set(p.signalType, bucket)
    }
    for (const p of preset.outputs) {
      const bucket = portCounts.get(p.signalType) ?? { in: 0, out: 0 }
      bucket.out += 1
      portCounts.set(p.signalType, bucket)
    }
    const portSummary = Array.from(portCounts.entries())
      .map(([signal, { in: i, out: o }]) => {
        const parts: string[] = []
        if (i > 0) parts.push(`${i}x ${signal} in`)
        if (o > 0) parts.push(`${o}x ${signal} out`)
        return parts.join(', ')
      })
      .filter(Boolean)
      .join(', ')
    return {
      name: preset.name,
      manufacturer: preset.manufacturer ?? '',
      category: preset.category,
      type: preset.type,
      ports: portSummary || 'none',
    }
  })
}

/**
 * Read a File as base64, stripping the data: URI prefix so the
 * backend receives only the raw payload.
 */
function fileToBase64(
  file: File,
): Promise<{ base64: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const match = result.match(/^data:(.+);base64,(.+)$/)
      if (!match) {
        reject(new Error('Unexpected FileReader output'))
        return
      }
      resolve({ mediaType: match[1], base64: match[2] })
    }
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'))
    reader.readAsDataURL(file)
  })
}

const ACCEPTED_MEDIA_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
])
const MAX_IMAGE_BYTES = 5 * 1024 * 1024 // 5MB — keeps base64 under API GW 10MB limit.

export function useIdentifyDevice() {
  return useMutation({
    mutationFn: async (file: File): Promise<IdentifyResult> => {
      if (file.size > MAX_IMAGE_BYTES) {
        throw new Error(
          `Image is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 5MB.`,
        )
      }
      const { base64, mediaType } = await fileToBase64(file)
      const normalizedMediaType = ACCEPTED_MEDIA_TYPES.has(mediaType)
        ? mediaType
        : 'image/jpeg'
      const catalog = buildCatalogSummary()

      const post = await apiClient.post<JobPostResponse>(
        `/api/v1/ai/identify-device`,
        { imageBase64: base64, mediaType: normalizedMediaType, catalog },
      )
      if (!post?.jobId) {
        throw new Error('Identify job could not be started')
      }

      const start = Date.now()
      while (Date.now() - start < POLL_TIMEOUT_MS) {
        await sleep(POLL_INTERVAL_MS)
        const status = await apiClient.get<JobStatusResponse>(
          `/api/v1/ai/jobs/${post.jobId}`,
        )
        if (status.status === 'done') {
          const raw = status.result?.matches ?? []
          const matches = raw
            .filter(
              m =>
                Number.isInteger(m.catalogIndex) &&
                m.catalogIndex >= 0 &&
                m.catalogIndex < DEVICE_PRESETS.length,
            )
            .map(m => ({
              preset: DEVICE_PRESETS[m.catalogIndex],
              confidence: m.confidence,
              reason: m.reason,
            }))
          return { matches, guess: normalizeGuess(status.result?.guess) }
        }
        if (status.status === 'failed') {
          throw new Error(status.error ?? 'Identify job failed')
        }
      }
      throw new Error(
        `Identify timed out after ${POLL_TIMEOUT_MS / 1000}s. Please try again.`,
      )
    },
  })
}

export type { AIDeviceMatch, IdentifyResponse }
